import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { CollectionEntry } from '@aeris/contracts'
import {
  ZERO_AMOUNT,
  amountToString,
  parseAmount,
  scheduleInstalments,
} from '@aeris/finance'
import { add, formatMoney, isPositive, subtract } from '@aeris/money'
import {
  Button,
  DateField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  MoneyInput,
  Select,
  Textarea,
} from '@aeris/ui'

import { renegotiateEntries } from '../lib/api/collections.ts'
import { ApiError } from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'

/**
 * Renegociar (F135): os títulos marcados saem, as parcelas novas entram.
 *
 * A prévia usa a MESMA `scheduleInstalments` que o servidor usa para gravar, e
 * é por isso que "3x de R$ 53,34 + 53,33 + 53,33" na tela é o que vai existir
 * depois de confirmar — não uma conta parecida feita em outro lugar.
 */

const localDay = (at: Date): string => {
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
}

/** Meio-dia local: um vencimento "dia 10" nunca vira dia 9 por fuso. */
const noonOf = (day: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (match === null) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)
}

export const RenegotiateDialog = ({
  partyId,
  customerName,
  entries,
  onClose,
}: {
  partyId: string
  customerName: string
  entries: readonly CollectionEntry[]
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [interest, setInterest] = useState('')
  const [discount, setDiscount] = useState('')
  const [count, setCount] = useState('3')
  const [interval, setInterval] = useState<'monthly' | 'days'>('monthly')
  const [intervalDays, setIntervalDays] = useState('30')
  const [firstDay, setFirstDay] = useState(() => {
    const inAMonth = new Date()
    inAMonth.setDate(inAMonth.getDate() + 30)
    return localDay(inAMonth)
  })
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const original = entries.reduce(
    (running, entry) => add(running, parseAmount(entry.outstanding)),
    ZERO_AMOUNT
  )
  const total = subtract(
    add(original, parseAmount(interest.length === 0 ? '0' : interest)),
    parseAmount(discount.length === 0 ? '0' : discount)
  )
  const instalments = Number.parseInt(count, 10)
  const days = Number.parseInt(intervalDays, 10)
  const firstDueAt = noonOf(firstDay)

  const schedule =
    firstDueAt === null ||
    !Number.isInteger(instalments) ||
    instalments < 1 ||
    instalments > 60 ||
    !isPositive(total) ||
    (interval === 'days' && (!Number.isInteger(days) || days < 1))
      ? null
      : scheduleInstalments({
          total,
          count: instalments,
          firstDueAt,
          interval,
          ...(interval === 'days' ? { intervalDays: days } : {}),
        })

  const plan = schedule?.ok === true ? schedule.instalments : null
  const ready = plan !== null && reason.trim().length >= 3

  const confirm = useMutation({
    mutationFn: async () =>
      await renegotiateEntries(partyId, {
        entries: entries.map((entry) => ({
          id: entry.id,
          version: entry.version,
        })),
        interestAmount: interest.length === 0 ? '0.00' : interest,
        discountAmount: discount.length === 0 ? '0.00' : discount,
        instalmentCount: instalments,
        firstDueAt: (firstDueAt ?? new Date()).toISOString(),
        interval,
        ...(interval === 'days' ? { intervalDays: days } : {}),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['collections'] })
      void queryClient.invalidateQueries({ queryKey: ['finance'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível renegociar.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Renegociar"
        description={`${String(entries.length)} ${entries.length === 1 ? 'título' : 'títulos'} de ${customerName} saem, e entram parcelas novas.`}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (!ready) return
            setError(null)
            confirm.mutate()
          }}
        >
          <ul className="flex flex-col divide-y divide-(--border-subtle) rounded-(--radius-md) border border-(--border-subtle) px-3">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex justify-between gap-3 py-1.5 text-(length:--text-sm)"
              >
                <span className="min-w-0 truncate text-(--text-secondary)">
                  {formatDate(entry.dueAt)} · {entry.description}
                </span>
                <span className="font-(--font-mono) tabular-nums">
                  {formatMoney(parseAmount(entry.outstanding))}
                </span>
              </li>
            ))}
            <li className="flex justify-between gap-3 py-1.5 text-(length:--text-sm) font-(--weight-medium)">
              <span>Total dos títulos</span>
              <span className="font-(--font-mono) tabular-nums">
                {formatMoney(original)}
              </span>
            </li>
          </ul>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Juros">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={interest}
                  onValueChange={setInterest}
                />
              )}
            </Field>
            <Field label="Desconto">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={discount}
                  onValueChange={setDiscount}
                />
              )}
            </Field>
            <Field label="Parcelas">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={count}
                  onChange={(event) => {
                    setCount(event.target.value.replace(/\D/g, ''))
                  }}
                />
              )}
            </Field>
            <Field label="Primeira parcela vence">
              {(field) => (
                <DateField
                  {...field}
                  value={firstDay}
                  onChange={(event) => {
                    setFirstDay(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="As outras vencem">
              {(field) => (
                <Select
                  {...field}
                  value={interval}
                  onChange={(event) => {
                    setInterval(event.target.value as 'monthly' | 'days')
                  }}
                >
                  <option value="monthly">todo mês, no mesmo dia</option>
                  <option value="days">a cada tantos dias</option>
                </Select>
              )}
            </Field>
            {interval === 'days' ? (
              <Field label="A cada quantos dias">
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    value={intervalDays}
                    onChange={(event) => {
                      setIntervalDays(event.target.value.replace(/\D/g, ''))
                    }}
                  />
                )}
              </Field>
            ) : null}
          </div>

          <section
            aria-label="Parcelas novas"
            className="flex flex-col gap-1 rounded-(--radius-md) bg-(--surface-sunken) p-3"
          >
            <span className="flex justify-between text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
              <span>Novo total</span>
              <span className="font-(--font-mono) tabular-nums">
                {isPositive(total) ? formatMoney(total) : '—'}
              </span>
            </span>
            {plan === null ? (
              <span className="text-(length:--text-xs) text-(--text-muted)">
                {isPositive(total)
                  ? 'Confira o número de parcelas e as datas.'
                  : 'O desconto não pode passar da dívida mais os juros.'}
              </span>
            ) : (
              <ol className="flex flex-col text-(length:--text-sm)">
                {plan.map((instalment) => (
                  <li
                    key={instalment.number}
                    className="flex justify-between gap-3"
                  >
                    <span className="text-(--text-secondary)">
                      {`${String(instalment.number)}/${String(plan.length)} · vence ${formatDate(instalment.dueAt.toISOString())}`}
                    </span>
                    <span
                      className="font-(--font-mono) tabular-nums"
                      data-amount={amountToString(instalment.amount)}
                    >
                      {formatMoney(instalment.amount)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <Field
            label="Motivo"
            hint="Fica na renegociação e no cancelamento dos títulos antigos"
          >
            {(field) => (
              <Textarea
                {...field}
                value={reason}
                maxLength={500}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
                placeholder="Ex.: cliente pediu para parcelar em 3x"
              />
            )}
          </Field>

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!ready}
              loading={confirm.isPending}
            >
              {plan === null
                ? 'Confirmar renegociação'
                : `Trocar ${String(entries.length)} ${entries.length === 1 ? 'título' : 'títulos'} por ${String(plan.length)} ${plan.length === 1 ? 'parcela' : 'parcelas'}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
